import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../AuthContext';
import MarqueeSettings from './MarqueeSettings';

/**
 * Composant pour la gestion des paramètres personnels de l'administrateur
 */
const Settings = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { currentUser } = useAuth();
  const [isPDG, setIsPDG] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        try {
          setLoading(true);
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setFormData({
              name: userData.name || '',
              email: userData.email || '',
              phone: userData.phone || '',
              position: userData.position || ''
            });
            setIsPDG(userData.role === 'PDG');
          }
        } catch (err) {
          console.error('Erreur lors du chargement des données:', err);
          setError('Impossible de charger vos informations. ' + err.message);
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchUserData();
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    
    try {
      setSaving(true);
      
      const updateData = {
        name: formData.name,
        phone: formData.phone
      };
      
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, updateData);
      
      setSuccessMessage('Vos informations ont été mises à jour avec succès.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError('Une erreur est survenue lors de la sauvegarde. ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, color: '#014527', fontWeight: 'bold' }}>
        Paramètres du compte
      </Typography>
      
      <Paper sx={{ p: 3, borderRadius: '10px', maxWidth: 600, mb: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Nom"
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
            required
          />
          
          <TextField
            fullWidth
            label="Email"
            name="email"
            value={formData.email}
            margin="normal"
            disabled
            sx={{ bgcolor: 'action.hover' }}
          />
          
          <TextField
            fullWidth
            label="Téléphone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            margin="normal"
          />
          
          <TextField
            fullWidth
            label="Poste"
            name="position"
            value={formData.position}
            margin="normal"
            disabled
            sx={{ bgcolor: 'action.hover' }}
          />
          
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            sx={{
              mt: 3,
              bgcolor: '#014527',
              '&:hover': { bgcolor: '#013320' }
            }}
          >
            {saving ? <CircularProgress size={24} /> : 'Enregistrer les modifications'}
          </Button>
        </form>
      </Paper>
      
      {/* Section pour le message défilant - accessible à tous les administrateurs */}
      <Divider sx={{ my: 4 }} />
      
      <MarqueeSettings />
    </Box>
  );
};

export default Settings;